import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'

/**
 * Apply a focal point offset to an image in order to better display its subject
 * at different viewports and aspect ratios. The offset uses `object-position`
 * and relies on `ojbect-fit: cover`.
 *
 * @param {HTMLImageElement} img The image element to apply the focal point to.
 * @param {number | null} fpX A number between -1 and 1, where 0 is
 *   the horiziontal center of the image, -1 is all the way left, and 1 is
 *   all the way right.
 * @param {number | null} fpY A number between -1 and 1, where 0 is
 *   the vertical center of the image, -1 is all the way to the top, and 1 is
 *   all the way to the bottom.
 */
class FocalPoint {
  emitter = GLOBAL_CONSTANTS.UTILS.EMITTER
  image = null
  picture = null
  sources = []

  /**
   * @param {HTMLImageElement | HTMLPictureElement} element
   */
  constructor(element) {
    // This class can operate on either an <img> tag directly, or on a <picture>
    // tag that may have multiple <source> elements.
    this.image = element.tagName.toLowerCase() === 'img'
      ? element
      : element.querySelector('img')

    if (this.image) {
      this.picture = this.image.closest('picture')
      this.sources = this.picture
        ? Array.from(this.picture.querySelectorAll('source'))
        : []

      this.init()
    }
  }

  init() {
    this.image.addEventListener('load', () => {
      this.onPossibleCurrentSrcChange()
    })

    if (this.image.complete) {
      requestAnimationFrame(() => {
        this.onPossibleCurrentSrcChange()
      })
    }

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.RESIZE, () => requestAnimationFrame(() => {
      this.onPossibleCurrentSrcChange()
    }))
  }

  /**
   * Callback for when the image's currentSrc might have changed.
   */
  onPossibleCurrentSrcChange() {
    let currentSource = null
    if (this.image.complete && this.image.currentSrc) {
      currentSource = this.sources.find(source =>
        source.srcset.includes(this.image.currentSrc) &&
        (!source.media || window.matchMedia(source.media).matches)
      )
    }

    // Since we can operate on either a <picture> tag or an <img> tag directly,
    // fall back to looking at the image itself if we don't have focal point
    // info from a current source.
    if (!currentSource && this.image.classList.contains('js-focal-point')) {
      currentSource = this.image
    }

    // Note that a value of zero is the same as not having a value, since zero
    // is the default position.
    let fpX = currentSource?.dataset.focusX || null
    let fpY = currentSource?.dataset.focusY || null

    // When there's no focal point data, remove the object-position and get out.
    if (fpX === null && fpY === null) {
      this.image.style.objectPosition = null
      return
    }

    // If we have at least one valid coordinate, we can proceed and just have
    // any missing coordinates default to 0 (centered).
    if (fpX === null && fpY !== null) {
      fpX = 0
    }

    if (fpY === null && fpX !== null) {
      fpY = 0
    }

    this.applyFocalPoint(parseFloat(fpX), parseFloat(fpY))
  }

  /**
   * Get the <source> element that corresponds to our currentSrc value.
   *
   * @returns {Element | undefined} The source element, if any.
   */
  getCurrentSourceElement() {
    return this.sources.find(source =>
      source.srcset.includes(this.image.currentSrc) &&
      (!source.media || window.matchMedia(source.media).matches)
    )
  }

  /**
   * Apply an object-position based on the given focal point percentages.
   *
   * @param {number} fpX A number between -1 and 1, where 0 is the
   *   the horiziontal center of the image, -1 is all the way left, and 1 is
   *   all the way right.
   * @param {number} fpY A number between -1 and 1, where 0 is the
   *   the vertical center of the image, -1 is all the way to the top, and 1 is
   *   all the way to the bottom.
   */
  applyFocalPoint(fpX, fpY) {
    // Translate our values between -1 and 1 into fractions between 0 and 1. The
    // fpY value is inverted from what we need from here on out, so account for
    // that here (instead of 75% from the bottom, we need 25% from the top, for
    // example).
    const fractionX = (fpX + 1) / 2
    const fractionY = 1 - ((fpY + 1) / 2)

    // Get our image's natural (on-disk) dimensions.
    const naturalWidth = this.image.naturalWidth
    const naturalHeight = this.image.naturalHeight
    const naturalAspectRatio = naturalWidth / naturalHeight

    // Get our image container's dimensions.
    const containerWidth = this.image.width
    const containerHeight = this.image.height
    const containerAspectRatio = containerWidth / containerHeight

    // Determine the scale factor of our image rendered in the DOM. Compare its
    // natural aspect ratio to the container's aspect ratio to determine if the
    // rendered image "overflows" vertically or horizontally. The dimension that
    // doesn't overflow matches the known container dimension. Comparing that to
    // our natural dimension gives us the proportion our image was scaled up or
    // down.
    const scaleFactor = naturalAspectRatio > containerAspectRatio
      ? containerHeight / naturalHeight
      : containerWidth / naturalWidth

    // Calculate the actual scaled dimensions of our image.
    const scaledWidth = naturalWidth * scaleFactor
    const scaledHeight = naturalHeight * scaleFactor

    // Calculate the actual pixel offset that places our focal point in the center
    // of the container. Note that `object-position` math can be tricky. Start
    // with the left edge of the image centered horizontally in the container,
    // then move the scaled image over whatever fraction of its width places the
    // focal point in the center instead. This article does a great job of
    // explaining the math.
    const offsetX = (containerWidth / 2) - (fractionX * scaledWidth)
    const offsetY = (containerHeight / 2) - (fractionY * scaledHeight)

    // Apply the position using clamp() to ensure the image always covers the
    // container.
    this.image.style.objectPosition = `clamp(100%, ${offsetX}px, 0%) clamp(100%, ${offsetY}px, 0%)`
  }
}

export default {
  selector: 'img.js-focal-point, picture.js-focal-point',
  Source: FocalPoint
}
